import Icon from '@ant-design/icons';
import { ReactNode } from 'react';
import { RouteAccessProps, RoutePermission } from 'types';
import { getPermission } from 'hooks/business/useNewPermisson';

import { ReactComponent as CancelTradeSvg } from './assets/cancelTrade.svg';
import { ReactComponent as SystemCancelTradeSvg } from './assets/systemCancelTrade.svg';
import { ReactComponent as OnlyRefundSvg } from './assets/onlyRefund.svg';
import { ReactComponent as RefusableSvg } from './assets/refusable.svg';
import { ReactComponent as ReviewSvg } from '../assets/review.svg';
import { ReactComponent as LogisticsSvg } from '../assets/logistics.svg';

export interface ScenarioRouteAccess {
  (
    props: RouteAccessProps & {
      platform?: General.PlatformUpperCaseTunnel;
      region?: General.UpperCaseRegion;
    },
  ): RoutePermission | false;
}

export const getRouteAccess =
  (permissionName: General.PermissionName): ScenarioRouteAccess =>
  ({ newPermissionEnable, newPermissionMap, platform, region }) =>
    getPermission({
      newPermissionEnable,
      newPermissionMap,
      permissionName,
      platform,
      region,
    })
      ? 'write'
      : false;

export type AideType = 'saveOrder' | 'review' | 'logistics';

export type Gather = 'cancelTrade' | 'systemCancelTrade' | 'returnTrade' | 'review' | 'logistics';

export const returnTradeScenarios: Marketing.ReturnTradeScenarioType[] = [
  'RETURN_TRADE_ITEM_MISSING',
  'RETURN_TRADE_WRONG_ITEM',
  'RETURN_TRADE_FUNCTIONAL_DMG',
  'RETURN_TRADE_ITEM_DIFF',
  'RETURN_TRADE_PHYSICAL_DMG',
  'RETURN_TRADE_EMPTY_PARCEL',
  'RETURN_TRADE_NOT_RECEIPT',
  'RETURN_TRADE_PRODUCT_NOT_IN_TIME',
  'RETURN_TRADE_ITEM_NOT_FIT',
  'RETURN_TRADE_OTHER',
];

export const isReturnTradeScenarioType = (
  scenarioType: any,
): scenarioType is Marketing.ReturnTradeScenarioType => returnTradeScenarios.includes(scenarioType);

export const cancelledTradeScenarios: Marketing.CancelledTradeScenarioType[] = [
  'CANCELLED_TRADE_CHANGE_VOUCHER',
  'CANCELLED_TRADE_MODIFY_ORDER',
  'CANCELLED_TRADE_CHANGE_MIND',
  'CANCELLED_TRADE_FOUND_CHEAPER',
  'CANCELLED_TRADE_TOO_TROUBLESOME',
  'CANCELLED_TRADE_OTHERS',
];

export const isCancelledTradeScenarioType = (
  scenarioType: any,
): scenarioType is Marketing.CancelledTradeScenarioType =>
  cancelledTradeScenarios.includes(scenarioType);

export const cancellingTradeScenarios: Marketing.CancellingTradeScenarioType[] = [
  'CANCELLING_TRADE_CHANGE_ADDRESS',
  'CANCELLING_TRADE_MODIFY_ORDER',
  'CANCELLING_TRADE_OTHERS',
  'CANCELLING_TRADE_IRRESPONDING',
  'CANCELLING_TRADE_CHANGE_MIND',
  'CANCELLING_TRADE_CHANGE_PAY_METHOD',
  'CANCELLING_TRADE_LONG_TIME_UNSHIPPED',
  'CANCELLING_TRADE_FOUND_CHEAPER',
  'CANCELLING_TRADE_WRONG_ORDER',
  'CANCELLING_TRADE_PAY_METHOD_DISABLED',
  'CANCELLING_TRADE_NEED_TO_USE_VOUCHER',
  'CANCELLING_TRADE_DISCOUNT_WORSE_THAN_EXPECTED',
  'CANCELLING_TRADE_FREIGHT_IS_TOO_HIGH',
];

export const systemCancelTradeScenarios: Marketing.SystemCancelTradeScenarioType[] = [
  'CANCELLING_DELIVERY_FAIL',
];

export const cancelTradeScenarios: Marketing.CancelTradeScenarioType[] = [
  ...cancellingTradeScenarios,
  ...cancelledTradeScenarios,
];

export const positiveReviewScenarios: Marketing.PositiveReviewScenarioType[] = [
  'POSITIVE_REVIEW_CARE',
  'POSITIVE_REVIEW_REPLY',
];

export const negativeReviewScenarios: Marketing.NegativeReviewScenarioType[] = [
  'NEGATIVE_REVIEW_SAVE',
  'NEGATIVE_REVIEW_REPLY',
];

export const reviewManagementScenarios: Marketing.ReviewManagementScenarioType[] = [
  ...positiveReviewScenarios,
  ...negativeReviewScenarios,
];

export const reviewScenarios: Marketing.ReviewScenarioType[] = [
  'PRAISE_REMINDER',
  ...reviewManagementScenarios,
];

export const logisticsScenarios: Marketing.LogisticsScenarioType[] = [
  'CONFIRM_TRADE',
  'DELAY_SHIP',
  'SHIPPED_REMINDER',
  'DELIVERY_REMINDER',
  'WAIT_DELIVERY_AGAIN',
  'SIGN_REMINDER',
];

export const isLogisticsScenarioType = (
  scenarioType: any,
): scenarioType is Marketing.LogisticsScenarioType => logisticsScenarios.includes(scenarioType);

export const noRegionScenarios: Marketing.NoRegionScenario[] = [
  'TIMEOUT_REPLY',
  'WELCOME_REPLY',
  'INVITE_FOLLOWER',
  'RECEPTION_SCORE',
];

export const isNoRegionScenario = (scenarioType: any): scenarioType is Marketing.NoRegionScenario =>
  noRegionScenarios.includes(scenarioType);

export const aideGathersMap: Record<AideType, Gather[]> = {
  saveOrder: ['returnTrade', 'cancelTrade', 'systemCancelTrade'],
  review: ['review'],
  logistics: ['logistics'],
};

export const gatherScenariosMap: Record<Gather, Marketing.AllScenarioType[]> = {
  returnTrade: returnTradeScenarios,
  cancelTrade: cancelTradeScenarios,
  systemCancelTrade: systemCancelTradeScenarios,
  review: reviewScenarios,
  logistics: logisticsScenarios,
};

export const aideTypeI18nLabelMap: Record<AideType, string> = {
  saveOrder: 'aide.弃单挽回',
  review: 'aide.评价管理',
  logistics: 'domain.aiReception.type.logisticsCare',
};

export const gatherI18nLabelMap: Record<Gather, string> = {
  cancelTrade: 'aide.买家取消订单',
  systemCancelTrade: 'aide.系统取消订单',
  returnTrade: 'domain.aiReception.type.refundOnlyOrReturnRefund',
  review: 'aide.评价管理',
  logistics: 'domain.aiReception.type.logisticsCare',
};

export const gatherIconMap: Record<Gather, ReactNode> = {
  cancelTrade: <Icon component={CancelTradeSvg} />,
  systemCancelTrade: <Icon component={SystemCancelTradeSvg} />,
  returnTrade: <Icon component={OnlyRefundSvg} />,
  review: <Icon component={ReviewSvg} />,
  logistics: <Icon component={LogisticsSvg} />,
};

export const gatherAideTypeMap: Record<Gather, AideType> = {
  cancelTrade: 'saveOrder',
  systemCancelTrade: 'saveOrder',
  returnTrade: 'saveOrder',
  review: 'review',
  logistics: 'logistics',
};

export type SingleScenario =
  | 'ORDER_REMINDER'
  | 'PAYMENT_REMINDER'
  | 'OUT_STOCK_REMINDER'
  | 'SIGNAL_COUPON'
  | 'TIMEOUT_REPLY'
  | 'WELCOME_REPLY'
  | 'INVITE_FOLLOWER'
  | 'RECEPTION_SCORE'
  | 'QUICK_PHRASE'
  | 'ORDER_FOLLOWUP';

const singleScenarios: SingleScenario[] = [
  'ORDER_REMINDER',
  'PAYMENT_REMINDER',
  'OUT_STOCK_REMINDER',
  'SIGNAL_COUPON',
  'TIMEOUT_REPLY',
  'WELCOME_REPLY',
  'INVITE_FOLLOWER',
  'RECEPTION_SCORE',
  'QUICK_PHRASE',
  'ORDER_FOLLOWUP',
];

export const isSingleScenarioType = (scenarioType: any): scenarioType is SingleScenario =>
  singleScenarios.includes(scenarioType);

export const scenarioTypeGatherMap: Record<
  Exclude<Marketing.AllScenarioType, SingleScenario>,
  Gather
> = {
  RETURN_TRADE_ITEM_MISSING: 'returnTrade',
  RETURN_TRADE_WRONG_ITEM: 'returnTrade',
  RETURN_TRADE_PHYSICAL_DMG: 'returnTrade',
  RETURN_TRADE_FUNCTIONAL_DMG: 'returnTrade',
  RETURN_TRADE_NOT_RECEIPT: 'returnTrade',
  RETURN_TRADE_ITEM_DIFF: 'returnTrade',
  RETURN_TRADE_EMPTY_PARCEL: 'returnTrade',
  RETURN_TRADE_PRODUCT_NOT_IN_TIME: 'returnTrade',
  RETURN_TRADE_ITEM_NOT_FIT: 'returnTrade',
  RETURN_TRADE_OTHER: 'returnTrade',
  CANCELLED_TRADE_CHANGE_VOUCHER: 'cancelTrade',
  CANCELLED_TRADE_MODIFY_ORDER: 'cancelTrade',
  CANCELLED_TRADE_CHANGE_MIND: 'cancelTrade',
  CANCELLED_TRADE_FOUND_CHEAPER: 'cancelTrade',
  CANCELLED_TRADE_TOO_TROUBLESOME: 'cancelTrade',
  CANCELLED_TRADE_OTHERS: 'cancelTrade',
  CANCELLING_TRADE_CHANGE_ADDRESS: 'cancelTrade',
  CANCELLING_TRADE_MODIFY_ORDER: 'cancelTrade',
  CANCELLING_TRADE_IRRESPONDING: 'cancelTrade',
  CANCELLING_TRADE_OTHERS: 'cancelTrade',
  CANCELLING_TRADE_CHANGE_MIND: 'cancelTrade',
  CANCELLING_TRADE_CHANGE_PAY_METHOD: 'cancelTrade',
  CANCELLING_TRADE_LONG_TIME_UNSHIPPED: 'cancelTrade',
  CANCELLING_TRADE_FOUND_CHEAPER: 'cancelTrade',
  CANCELLING_TRADE_WRONG_ORDER: 'cancelTrade',
  CANCELLING_TRADE_PAY_METHOD_DISABLED: 'cancelTrade',
  CANCELLING_TRADE_NEED_TO_USE_VOUCHER: 'cancelTrade',
  CANCELLING_TRADE_DISCOUNT_WORSE_THAN_EXPECTED: 'cancelTrade',
  CANCELLING_TRADE_FREIGHT_IS_TOO_HIGH: 'cancelTrade',
  CANCELLING_DELIVERY_FAIL: 'systemCancelTrade',
  PRAISE_REMINDER: 'review',
  NEGATIVE_REVIEW_SAVE: 'review',
  NEGATIVE_REVIEW_REPLY: 'review',
  POSITIVE_REVIEW_CARE: 'review',
  POSITIVE_REVIEW_REPLY: 'review',
  CONFIRM_TRADE: 'logistics',
  DELAY_SHIP: 'logistics',
  SHIPPED_REMINDER: 'logistics',
  WAIT_FOR_TAX: 'logistics',
  DELIVERY_REMINDER: 'logistics',
  WAIT_DELIVERY_AGAIN: 'logistics',
  SIGN_REMINDER: 'logistics',
};

export const scenarioTypeI18nLabelMap: Record<Marketing.AllScenarioType, string> = {
  RETURN_TRADE_ITEM_MISSING: 'aide.收到货物少件',
  RETURN_TRADE_WRONG_ITEM: 'aide.收到货物错误',
  RETURN_TRADE_PHYSICAL_DMG: 'aide.收到货物破损',
  RETURN_TRADE_FUNCTIONAL_DMG: 'aide.收到货物无法使用',
  RETURN_TRADE_NOT_RECEIPT: 'aide.未收到货物',
  RETURN_TRADE_ITEM_DIFF: 'aide.收到货物和介绍不相符',
  RETURN_TRADE_EMPTY_PARCEL: 'aide.收到空包裹',
  RETURN_TRADE_PRODUCT_NOT_IN_TIME: 'aide.产品未及时送达',
  RETURN_TRADE_ITEM_NOT_FIT: 'scenario_RETURN_TRADE_ITEM_NOT_FIT',
  RETURN_TRADE_OTHER: 'scenario_RETURN_TRADE_OTHER',
  CANCELLED_TRADE_CHANGE_VOUCHER: 'refundSetting.修改优惠券',
  CANCELLED_TRADE_MODIFY_ORDER: 'aide.修改订单',
  CANCELLED_TRADE_CHANGE_MIND: 'aide.不想买了',
  CANCELLED_TRADE_FOUND_CHEAPER: 'aide.在别处找到更便宜的',
  CANCELLED_TRADE_TOO_TROUBLESOME: 'aide.付款程序太麻烦',
  CANCELLED_TRADE_OTHERS: 'aide.其他',
  CANCELLING_TRADE_CHANGE_ADDRESS: 'refundSetting.修改收货地址',
  CANCELLING_TRADE_MODIFY_ORDER: 'aide.修改已有订单',
  CANCELLING_TRADE_OTHERS: 'aide.其他改变主意',
  CANCELLING_TRADE_IRRESPONDING: 'aide.卖家不响应咨询',
  CANCELLING_TRADE_CHANGE_MIND: 'aide.改变主意',
  CANCELLING_TRADE_CHANGE_PAY_METHOD: 'aide.修改支付方式',
  CANCELLING_TRADE_LONG_TIME_UNSHIPPED: 'aide.长时间未发货',
  CANCELLING_TRADE_FOUND_CHEAPER: 'aide.在别处找到更便宜的',
  CANCELLING_TRADE_WRONG_ORDER: 'aide.误创建订单',
  CANCELLING_TRADE_PAY_METHOD_DISABLED: 'aide.所需付款方式不可用',
  CANCELLING_TRADE_NEED_TO_USE_VOUCHER: 'aide.需使用优惠券',
  CANCELLING_TRADE_DISCOUNT_WORSE_THAN_EXPECTED: 'aide.折扣不如预期',
  CANCELLING_TRADE_FREIGHT_IS_TOO_HIGH: 'aide.运费过高',
  CANCELLING_DELIVERY_FAIL: 'aide.派送失败',
  ORDER_REMINDER: 'marketing.催下单',
  PAYMENT_REMINDER: 'marketing.催付款',
  PRAISE_REMINDER: 'marketing.催好评',
  NEGATIVE_REVIEW_SAVE: 'domain.aiReception.type.negativeReviewRecovery',
  NEGATIVE_REVIEW_REPLY: 'aide.中差评自动回评',
  POSITIVE_REVIEW_CARE: 'domain.aiReception.type.positiveReviewFollowUp',
  POSITIVE_REVIEW_REPLY: 'aide.好评自动回评',
  CONFIRM_TRADE: 'domain.aiReception.type.orderConfirmed',
  DELAY_SHIP: 'domain.aiReception.type.delayedShipment',
  SHIPPED_REMINDER: 'domain.aiReception.type.shipped',
  WAIT_FOR_TAX: 'domain.aiReception.type.waitForTaxPayment',
  DELIVERY_REMINDER: 'domain.aiReception.type.lastMileDelivery',
  WAIT_DELIVERY_AGAIN: 'domain.aiReception.type.waitingForRedelivery',
  SIGN_REMINDER: 'domain.aiReception.type.received',
  OUT_STOCK_REMINDER: 'domain.aiReception.type.outOfStockReminder',
  TIMEOUT_REPLY: 'nav.autoReply',
  WELCOME_REPLY: 'domain.aiReception.type.storeWelcomeMessage',
  INVITE_FOLLOWER: 'auto_invite_follow',
  RECEPTION_SCORE: 'aide.接待评分',
  QUICK_PHRASE: 'phrase.快捷回复',
  SIGNAL_COUPON: 'domain.aiReception.type.passphraseToClaimCode',
  ORDER_FOLLOWUP: '问题订单跟进',
};

export const scenarioPermissionMap: Record<Marketing.AllScenarioType, General.PermissionName> = {
  RETURN_TRADE_ITEM_MISSING: '配置弃单挽回任务',
  RETURN_TRADE_WRONG_ITEM: '配置弃单挽回任务',
  RETURN_TRADE_PHYSICAL_DMG: '配置弃单挽回任务',
  RETURN_TRADE_FUNCTIONAL_DMG: '配置弃单挽回任务',
  RETURN_TRADE_NOT_RECEIPT: '配置弃单挽回任务',
  RETURN_TRADE_ITEM_DIFF: '配置弃单挽回任务',
  RETURN_TRADE_EMPTY_PARCEL: '配置弃单挽回任务',
  RETURN_TRADE_PRODUCT_NOT_IN_TIME: '配置弃单挽回任务',
  RETURN_TRADE_ITEM_NOT_FIT: '配置弃单挽回任务',
  RETURN_TRADE_OTHER: '配置弃单挽回任务',
  CANCELLED_TRADE_CHANGE_VOUCHER: '配置弃单挽回任务',
  CANCELLED_TRADE_MODIFY_ORDER: '配置弃单挽回任务',
  CANCELLED_TRADE_CHANGE_MIND: '配置弃单挽回任务',
  CANCELLED_TRADE_FOUND_CHEAPER: '配置弃单挽回任务',
  CANCELLED_TRADE_TOO_TROUBLESOME: '配置弃单挽回任务',
  CANCELLED_TRADE_OTHERS: '配置弃单挽回任务',
  CANCELLING_TRADE_CHANGE_ADDRESS: '配置弃单挽回任务',
  CANCELLING_TRADE_MODIFY_ORDER: '配置弃单挽回任务',
  CANCELLING_TRADE_OTHERS: '配置弃单挽回任务',
  CANCELLING_TRADE_IRRESPONDING: '配置弃单挽回任务',
  CANCELLING_TRADE_CHANGE_MIND: '配置弃单挽回任务',
  CANCELLING_TRADE_CHANGE_PAY_METHOD: '配置弃单挽回任务',
  CANCELLING_TRADE_LONG_TIME_UNSHIPPED: '配置弃单挽回任务',
  CANCELLING_TRADE_FOUND_CHEAPER: '配置弃单挽回任务',
  CANCELLING_TRADE_WRONG_ORDER: '配置弃单挽回任务',
  CANCELLING_TRADE_PAY_METHOD_DISABLED: '配置弃单挽回任务',
  CANCELLING_TRADE_NEED_TO_USE_VOUCHER: '配置弃单挽回任务',
  CANCELLING_TRADE_DISCOUNT_WORSE_THAN_EXPECTED: '配置弃单挽回任务',
  CANCELLING_TRADE_FREIGHT_IS_TOO_HIGH: '配置弃单挽回任务',
  CANCELLING_DELIVERY_FAIL: '配置弃单挽回任务',
  ORDER_REMINDER: '配置催单催付任务',
  PAYMENT_REMINDER: '配置催单催付任务',
  PRAISE_REMINDER: '配置评价管理任务',
  NEGATIVE_REVIEW_SAVE: '配置评价管理任务',
  NEGATIVE_REVIEW_REPLY: '配置评价管理任务',
  POSITIVE_REVIEW_CARE: '配置评价管理任务',
  POSITIVE_REVIEW_REPLY: '配置评价管理任务',
  CONFIRM_TRADE: '配置物流呵护任务',
  DELAY_SHIP: '配置物流呵护任务',
  SHIPPED_REMINDER: '配置物流呵护任务',
  WAIT_FOR_TAX: '配置物流呵护任务',
  DELIVERY_REMINDER: '配置物流呵护任务',
  WAIT_DELIVERY_AGAIN: '配置物流呵护任务',
  SIGN_REMINDER: '配置物流呵护任务',
  OUT_STOCK_REMINDER: '配置缺货提醒任务',
  TIMEOUT_REPLY: '超时自动回复',
  WELCOME_REPLY: '进店欢迎语',
  INVITE_FOLLOWER: '进店欢迎语',
  RECEPTION_SCORE: '配置接待评分任务',
  QUICK_PHRASE: '快捷回复-个人模版',
  SIGNAL_COUPON: '超时自动回复',
  ORDER_FOLLOWUP: '配置缺货提醒任务',
};

export const cancellingIcon = <Icon component={RefusableSvg} />;

export const scenarioIconMap: Partial<Record<Marketing.AllScenarioType, ReactNode>> = {};

export const scenarioTypeTipsMap: Partial<Record<Marketing.AllScenarioType, string>> = {
  NEGATIVE_REVIEW_SAVE: 'aide.买家符合设置的评价星级条件时自动发送安抚话术，提升中差评挽回率',
  WAIT_FOR_TAX: 'aide.等待缴税tips',
};

export const singleScenarioTypePathMap: Record<SingleScenario, string> = {
  ORDER_REMINDER: 'order',
  PAYMENT_REMINDER: 'pay',
  OUT_STOCK_REMINDER: 'outstock',
  SIGNAL_COUPON: 'basis/signal',
  TIMEOUT_REPLY: 'basis/autoReply',
  WELCOME_REPLY: 'basis/welcome',
  INVITE_FOLLOWER: 'basis/inviteFollower',
  QUICK_PHRASE: 'basis/phrase',
  RECEPTION_SCORE: 'basis/score',
  ORDER_FOLLOWUP: 'outstock',
};

export const marketingScenarios: Marketing.ScenarioType[] = [
  'ORDER_REMINDER',
  'PAYMENT_REMINDER',
  'PRAISE_REMINDER',
];

// 如果在 marketingScenarios 中的场景是有轮次
export const isMarketingScenarioType = (
  scenarioType: any,
): scenarioType is Marketing.ScenarioType => marketingScenarios.includes(scenarioType);

export const scenarioTriggerActionI18nMap = {
  ORDER_REMINDER: 'marketing.会话结束',
  INVITE_FOLLOWER: 'marketing.会话结束',
  PAYMENT_REMINDER: 'marketing.买家下单',
  PRAISE_REMINDER: {
    SIGNED: 'marketing.买家签收',
    CONFIRMED: 'marketing.买家确认收货',
  },
} as const;

export const scenarioFirstRoundMinTimeMap: Record<
  Marketing.ScenarioType | 'INVITE_FOLLOWER',
  number
> = {
  ORDER_REMINDER: 1,
  PAYMENT_REMINDER: 1,
  PRAISE_REMINDER: 0,
  INVITE_FOLLOWER: 1,
};

export const supportLazadaScenarios: Marketing.AllScenarioType[] = [
  'QUICK_PHRASE',
  'ORDER_REMINDER',
  'PAYMENT_REMINDER',
  'NEGATIVE_REVIEW_REPLY',
  'POSITIVE_REVIEW_REPLY',
  'CONFIRM_TRADE',
  'SHIPPED_REMINDER',
  'TIMEOUT_REPLY',
  'WELCOME_REPLY',
  'INVITE_FOLLOWER',
  'PRAISE_REMINDER',
  'DELIVERY_REMINDER',
  'SIGN_REMINDER',
  'RECEPTION_SCORE',
  'DELAY_SHIP',
  'WAIT_DELIVERY_AGAIN',
  ...returnTradeScenarios,
  ...cancelTradeScenarios,
  ...systemCancelTradeScenarios,
];

export const lazadaUnLimitMessageCountScenarios: Marketing.AllScenarioType[] = [
  'QUICK_PHRASE',
  'NEGATIVE_REVIEW_REPLY',
  'POSITIVE_REVIEW_REPLY',
];

export const supportTiktokScenarios: Marketing.AllScenarioType[] = [
  'WELCOME_REPLY',
  'TIMEOUT_REPLY',
  'ORDER_REMINDER',
  'QUICK_PHRASE',
  'PAYMENT_REMINDER',
  'PRAISE_REMINDER',
  'OUT_STOCK_REMINDER',
  'ORDER_FOLLOWUP',
  'CONFIRM_TRADE',
  'SHIPPED_REMINDER',
  'DELIVERY_REMINDER',
  'SIGN_REMINDER',
  'RECEPTION_SCORE',
  'DELAY_SHIP',
  'WAIT_DELIVERY_AGAIN',
  ...returnTradeScenarios,
  ...cancelTradeScenarios,
  ...systemCancelTradeScenarios,
];

export const supportTiktokReviewScenarios: Marketing.AllScenarioType[] = [
  'POSITIVE_REVIEW_CARE',
  'POSITIVE_REVIEW_REPLY',
  'NEGATIVE_REVIEW_SAVE',
  'NEGATIVE_REVIEW_REPLY',
];

export const supportMixingTokopediaScenarios: Marketing.AllScenarioType[] = [
  'ORDER_REMINDER',
  'CONFIRM_TRADE',
  'SHIPPED_REMINDER',
  'TIMEOUT_REPLY',
  'RECEPTION_SCORE',
];

export const supportTokopediaScenarios: Marketing.AllScenarioType[] = [
  'QUICK_PHRASE',
  'TIMEOUT_REPLY',
  'WELCOME_REPLY',
  'RECEPTION_SCORE',
];

export const supportAliexpressScenarios: Marketing.AllScenarioType[] = [
  'QUICK_PHRASE',
  'WELCOME_REPLY',
  'TIMEOUT_REPLY',
  'ORDER_REMINDER',
  'PAYMENT_REMINDER',
  'DELIVERY_REMINDER',
  'SIGN_REMINDER',
];

export const mercadoReturnTradeScenarios: Marketing.ReturnTradeScenarioType[] = [
  'RETURN_TRADE_ITEM_DIFF',
  'RETURN_TRADE_FUNCTIONAL_DMG',
  'RETURN_TRADE_ITEM_MISSING',
  'RETURN_TRADE_PHYSICAL_DMG',
  'RETURN_TRADE_EMPTY_PARCEL',
  'RETURN_TRADE_WRONG_ITEM',
  'RETURN_TRADE_ITEM_NOT_FIT',
  'RETURN_TRADE_OTHER',
];

export const supportMercadoScenarios: Marketing.AllScenarioType[] = [
  'QUICK_PHRASE',
  'TIMEOUT_REPLY',
  'WELCOME_REPLY',
  ...mercadoReturnTradeScenarios,
  'PRAISE_REMINDER',
  'POSITIVE_REVIEW_CARE',
  'NEGATIVE_REVIEW_SAVE',
  'CONFIRM_TRADE',
  'SHIPPED_REMINDER',
  'SIGN_REMINDER',
];

export const supportKwaiScenarios: Marketing.AllScenarioType[] = [
  'QUICK_PHRASE',
  'TIMEOUT_REPLY',
  'WELCOME_REPLY',
];

export const supportFacebookScenarios: Marketing.AllScenarioType[] = ['QUICK_PHRASE'];

export const shopeeReturnTradeScenarios: Marketing.ReturnTradeScenarioType[] = [
  'RETURN_TRADE_NOT_RECEIPT',
  'RETURN_TRADE_ITEM_MISSING',
  'RETURN_TRADE_WRONG_ITEM',
  'RETURN_TRADE_PHYSICAL_DMG',
  'RETURN_TRADE_FUNCTIONAL_DMG',
  'RETURN_TRADE_OTHER',
];

export const lazadaReturnTradeScenarios: Marketing.ReturnTradeScenarioType[] = [
  'RETURN_TRADE_ITEM_MISSING',
  'RETURN_TRADE_WRONG_ITEM',
  'RETURN_TRADE_ITEM_DIFF',
  'RETURN_TRADE_FUNCTIONAL_DMG',
  'RETURN_TRADE_PHYSICAL_DMG',
  'RETURN_TRADE_EMPTY_PARCEL',
  'RETURN_TRADE_NOT_RECEIPT',
];

export const tiktokReturnTradeScenarios: Marketing.ReturnTradeScenarioType[] = [
  'RETURN_TRADE_PRODUCT_NOT_IN_TIME',
  'RETURN_TRADE_FUNCTIONAL_DMG',
  'RETURN_TRADE_NOT_RECEIPT',
  'RETURN_TRADE_PHYSICAL_DMG',
  'RETURN_TRADE_ITEM_DIFF',
  'RETURN_TRADE_WRONG_ITEM',
  'RETURN_TRADE_ITEM_MISSING',
];

export const shopeeCancelTradeScenarios: Marketing.CancelTradeScenarioType[] = [
  'CANCELLING_TRADE_CHANGE_ADDRESS',
  'CANCELLING_TRADE_MODIFY_ORDER',
  'CANCELLING_TRADE_OTHERS',
  'CANCELLING_TRADE_IRRESPONDING',
  ...cancelledTradeScenarios,
];

export const lazadaCancelTradeScenarios: Marketing.CancelTradeScenarioType[] = [
  'CANCELLING_TRADE_CHANGE_MIND',
  'CANCELLING_TRADE_CHANGE_PAY_METHOD',
  'CANCELLING_TRADE_LONG_TIME_UNSHIPPED',
  'CANCELLING_TRADE_FOUND_CHEAPER',
  'CANCELLING_TRADE_MODIFY_ORDER',
  'CANCELLING_TRADE_CHANGE_ADDRESS',
];

export const tiktokCancelTradeScenarios: Marketing.CancelTradeScenarioType[] = [
  'CANCELLING_TRADE_WRONG_ORDER',
  'CANCELLING_TRADE_OTHERS',
  'CANCELLING_TRADE_CHANGE_ADDRESS',
  'CANCELLING_TRADE_PAY_METHOD_DISABLED',
  'CANCELLING_TRADE_CHANGE_PAY_METHOD',
  'CANCELLING_TRADE_MODIFY_ORDER',
  'CANCELLING_TRADE_FOUND_CHEAPER',
  'CANCELLING_TRADE_CHANGE_MIND',
  'CANCELLING_TRADE_NEED_TO_USE_VOUCHER',
  'CANCELLING_TRADE_LONG_TIME_UNSHIPPED',
  'CANCELLING_TRADE_DISCOUNT_WORSE_THAN_EXPECTED',
  'CANCELLING_TRADE_FREIGHT_IS_TOO_HIGH',
];

export const platformGatherScenariosMap: Partial<
  Record<General.PlatformUpperCase, Partial<Record<Gather, Marketing.AllScenarioType[]>>>
> = {
  SHOPEE: {
    returnTrade: shopeeReturnTradeScenarios,
    cancelTrade: shopeeCancelTradeScenarios,
  },
  LAZADA: {
    returnTrade: lazadaReturnTradeScenarios,
    cancelTrade: lazadaCancelTradeScenarios,
  },
  TIKTOK: {
    returnTrade: tiktokReturnTradeScenarios,
    cancelTrade: tiktokCancelTradeScenarios,
  },
  TIKTOK_US: {
    returnTrade: tiktokReturnTradeScenarios,
    cancelTrade: tiktokCancelTradeScenarios,
  },
  MERCADO: {
    returnTrade: mercadoReturnTradeScenarios,
    cancelTrade: [],
    systemCancelTrade: [],
  },
};

const getRedirectScenarioMap = (
  allScenarios: Marketing.AllScenarioType[],
  targetScenarios: Marketing.AllScenarioType[],
) => {
  return allScenarios.reduce<Partial<Record<Marketing.AllScenarioType, Marketing.AllScenarioType>>>(
    (res, scenario) => {
      if (targetScenarios.includes(scenario)) {
        return res;
      }

      return {
        ...res,
        [scenario]: targetScenarios[0],
      };
    },
    {},
  );
};

export const platformRedirectScenarioMap = Object.keys(platformGatherScenariosMap).reduce<
  Partial<
    Record<
      General.PlatformUpperCase,
      Partial<Record<Marketing.AllScenarioType, Marketing.AllScenarioType>>
    >
  >
>((res, platform) => {
  const { [platform]: gatherTargetScenariosMap } = platformGatherScenariosMap;

  if (!gatherTargetScenariosMap) {
    return res;
  }

  res[platform] = Object.keys(gatherTargetScenariosMap).reduce((res, gather) => {
    const { [gather]: gargetScenarios = [] } = gatherTargetScenariosMap;

    if (gargetScenarios.length === 0) {
      return res;
    }

    return {
      ...res,
      ...getRedirectScenarioMap(gatherScenariosMap[gather], gargetScenarios),
    };
  }, {});

  return res;
}, {});

export const isReviewManagementScenarioType = (
  scenarioType: any,
): scenarioType is Marketing.ReviewManagementScenarioType =>
  reviewManagementScenarios.includes(scenarioType);

export const isPositiveReviewScenarioType = (
  scenarioType: any,
): scenarioType is Marketing.PositiveReviewScenarioType =>
  positiveReviewScenarios.includes(scenarioType);
export const isNegativeReviewScenarioType = (
  scenarioType: any,
): scenarioType is Marketing.NegativeReviewScenarioType =>
  negativeReviewScenarios.includes(scenarioType);

export const positiveReviewStars = [4, 5];
export const negativeReviewStars = [1, 2, 3, 4];

type ConflictScenarioType = 'PRAISE_REMINDER' | 'SIGN_REMINDER';

export const conflictScenarios: ConflictScenarioType[] = ['PRAISE_REMINDER', 'SIGN_REMINDER'];

export const isConfictScenarioType = (scenarioType: any): scenarioType is ConflictScenarioType =>
  conflictScenarios.includes(scenarioType);

// 隐藏操作日志入口的场景
export const hideOperateLogScenarioTypes: Marketing.AllScenarioType[] = [
  'TIMEOUT_REPLY',
  'WELCOME_REPLY',
  'INVITE_FOLLOWER',
  'RECEPTION_SCORE',
];

export const paymentRelatedScenarios: Marketing.AllScenarioType[] = [
  'PAYMENT_REMINDER',
  ...logisticsScenarios,
  ...returnTradeScenarios,
  ...cancelTradeScenarios,
  ...systemCancelTradeScenarios,
  ...reviewScenarios,
];

type SpecialPermissionConfig = Partial<
  Record<General.PlatformUpperCase, General.UpperCaseRegion[]>
>;

export const specialPermissionMap: Partial<
  Record<Marketing.AllScenarioType, SpecialPermissionConfig>
> = {
  WAIT_FOR_TAX: {
    SHOPEE: ['BR'],
  },
};
